import React, { useState, useCallback } from 'react';
import { AssetSlot } from './AssetSlot';
import { TextInputs, EMPTY_TEXT_FIELDS } from './TextInputs';
import { useAssets } from '../../hooks/useAssets';
import { useEditorContext } from '../../context/EditorContext';
import type { TextFields, SegmentsProps } from './TextInputs';
import type { AssetKey } from '../../types';

// ─── Asset slot definitions ───────────────────────────────────

interface SlotDef {
  key: AssetKey;
  label: string;
  required?: boolean;
  hint?: string;
  group?: string;
}

const MEDIA_SLOTS: SlotDef[] = [
  { key: 'logo', label: 'Channel Logo', required: true, hint: 'PNG or SVG, transparent background' },
  { key: 'anchorVideo', label: 'Anchor / Presenter Video', hint: 'MP4 recommended' },
  { key: 'voiceAudio', label: 'Voice Over Audio', hint: 'MP3, M4A or WAV' },
  { key: 'backgroundImage', label: 'Background Image', hint: 'JPEG or PNG, 1920×1080' },
  { key: 'backgroundVideo', label: 'Background Video', hint: 'MP4, loopable' },
  { key: 'backgroundMusic', label: 'Background Music', hint: 'MP3 or WAV, will be mixed at low volume' },
  { key: 'watermark', label: 'Watermark', hint: 'PNG with transparency' },
];

// Overlay slots are derived dynamically from the current template's layers
// (see useOverlaySlots hook below) — no fixed list here.

type Tab = 'media' | 'text';

// ─── Props ────────────────────────────────────────────────────

interface AssetManagerProps extends SegmentsProps {
  textFields: TextFields;
  onTextChange: (field: keyof TextFields, value: string) => void;
}

// ─── Component ───────────────────────────────────────────────

export function AssetManager({
  textFields,
  onTextChange,
  headlineSegments,
  subheadlineSegments,
  onAddHeadlineSegment,
  onChangeHeadlineSegment,
  onRemoveHeadlineSegment,
  onAddSubheadlineSegment,
  onChangeSubheadlineSegment,
  onRemoveSubheadlineSegment,
}: AssetManagerProps) {
  const [tab, setTab] = useState<Tab>('media');
  const [showOverlays, setShowOverlays] = useState(false);
  const { assets, uploading, errors, upload, remove } = useAssets();

  // Build dynamic overlay slot list from current template layers
  const { state: editorState } = useEditorContext();
  const overlaySlots: SlotDef[] = (editorState.editableConfig?.layers ?? [])
    .filter((l) => l.type === 'image' || l.type === 'video')
    .map((l) => ({
      key: (l as { assetKey: string }).assetKey as AssetKey,
      label: `${l.type === 'image' ? '🖼' : '▶'} ${l.label || l.id}`,
      hint: l.type === 'image' ? 'JPEG, PNG or WebP' : 'MP4 or WebM',
    }))
    // de-duplicate by key (two layers shouldn't share assetKey, but just in case)
    .filter((s, i, arr) => arr.findIndex((x) => x.key === s.key) === i)
    .sort((a, b) => a.key.localeCompare(b.key));

  const handleUpload = useCallback(
    (key: AssetKey) => (file: File) => {
      void upload(file, key);
    },
    [upload]
  );

  const handleRemove = useCallback(
    (key: AssetKey) => () => {
      remove(key);
    },
    [remove]
  );

  return (
    <section className="flex flex-col h-full" aria-label="Asset manager">
      {/* Panel header */}
      <div className="px-4 py-3 border-b border-zinc-700 shrink-0">
        <h2 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">
          Assets
        </h2>
        {/* Tabs */}
        <div className="flex gap-1 bg-zinc-800 rounded-md p-0.5">
          <TabButton active={tab === 'media'} onClick={() => setTab('media')}>
            Media
          </TabButton>
          <TabButton active={tab === 'text'} onClick={() => setTab('text')}>
            Text
          </TabButton>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-3">
        {tab === 'media' ? (
          <div className="space-y-4">
            {MEDIA_SLOTS.map(({ key, label, required, hint }) => (
              <AssetSlot
                key={key}
                assetKey={key}
                label={label}
                required={required}
                hint={hint}
                asset={assets[key]}
                isUploading={uploading[key] ?? false}
                error={errors[key]}
                onUpload={handleUpload(key)}
                onRemove={handleRemove(key)}
              />
            ))}

            {/* Image/Video overlays — dynamic, based on layers in the template */}
            {overlaySlots.length > 0 && (
              <div>
                <button
                  type="button"
                  onClick={() => setShowOverlays((v) => !v)}
                  className="w-full flex items-center justify-between py-1.5 text-xs font-semibold text-zinc-400 uppercase tracking-wider hover:text-zinc-200 transition-colors"
                >
                  <span>Image / Video Overlays ({overlaySlots.length})</span>
                  <span>{showOverlays ? '▲' : '▼'}</span>
                </button>
                {showOverlays && (
                  <div className="space-y-4 mt-2">
                    {overlaySlots.map(({ key, label, hint }) => (
                      <AssetSlot
                        key={key}
                        assetKey={key}
                        label={label}
                        hint={hint}
                        asset={assets[key]}
                        isUploading={uploading[key] ?? false}
                        error={errors[key]}
                        onUpload={handleUpload(key)}
                        onRemove={handleRemove(key)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
            {overlaySlots.length === 0 && (
              <p className="text-[10px] text-zinc-600 text-center py-2">
                Add image/video overlay layers from the <strong className="text-zinc-500">Layers</strong> panel, then upload assets here.
              </p>
            )}
          </div>
        ) : (
          <TextInputs
            values={textFields}
            onChange={onTextChange}
            headlineSegments={headlineSegments}
            subheadlineSegments={subheadlineSegments}
            onAddHeadlineSegment={onAddHeadlineSegment}
            onChangeHeadlineSegment={onChangeHeadlineSegment}
            onRemoveHeadlineSegment={onRemoveHeadlineSegment}
            onAddSubheadlineSegment={onAddSubheadlineSegment}
            onChangeSubheadlineSegment={onChangeSubheadlineSegment}
            onRemoveSubheadlineSegment={onRemoveSubheadlineSegment}
          />
        )}
      </div>
    </section>
  );
}

// ─── Tab button ───────────────────────────────────────────────

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'flex-1 py-1 text-xs font-medium rounded transition-colors',
        active
          ? 'bg-zinc-700 text-white shadow-sm'
          : 'text-zinc-400 hover:text-zinc-200',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

// ─── Re-export TextFields type for App.tsx ────────────────────
export type { TextFields };
export { EMPTY_TEXT_FIELDS };
